/**
 * 案件管理と陸送員スケジュール関連のカスタムフック
 *
 * MasterDataContextと連携して、案件表示に必要な全ての
 * ヘルパー関数を提供します。メモ化により最適化されています。
 */

import { useMemo, useCallback } from 'react';
import { useMasterData } from './useMasterData';
import {
  getVehicleName as getVehicleNameHelper,
  getVehicleNameForSchedule as getVehicleNameForScheduleHelper,
  getBusinessPartnerName as getBusinessPartnerNameHelper,
  getPartnerNameByRole as getPartnerNameByRoleHelper,
  getTransporterName as getTransporterNameHelper,
  getCaseStatusText as getCaseStatusTextHelper,
  getCaseStatusColor as getCaseStatusColorHelper,
  getPickupPrefecture as getPickupPrefectureHelper,
} from '@/utils/caseHelpers';
import { formatDateForDisplay } from '@/constants/dates';
import type { Case } from '@/types';

/**
 * 案件ヘルパー関数のカスタムフック
 *
 * MasterDataContextから取得したデータを使って、
 * 案件表示に必要な全てのヘルパー関数を提供します。
 *
 * @returns ヘルパー関数とデータアクセス関数のオブジェクト
 */
export const useCaseHelpers = () => {
  const {
    clients,
    landTransporters,
    getVehicleModelById,
    getManufacturerById,
    getBusinessPartnerById,
    getBusinessPartnerLocationById,
    loading,
  } = useMasterData();

  /**
   * 車両名を取得する関数（メモ化済み）
   */
  const getVehicleName = useCallback(
    (case_: Case): string => {
      return getVehicleNameHelper(case_, getVehicleModelById, getManufacturerById);
    },
    [getVehicleModelById, getManufacturerById]
  );

  /**
   * 陸送員スケジュール画面専用: 車両モデル名のみを取得（メーカー名を除外）
   *
   * スケジュール画面では表示スペースが限られているため、
   * 「トヨタ クラウン」 → 「クラウン」のようにメーカー名を除外して表示します。
   *
   * @see getVehicleNameForScheduleHelper
   */
  const getVehicleNameForSchedule = useCallback(
    (case_: Case): string => {
      return getVehicleNameForScheduleHelper(case_, getVehicleModelById, getManufacturerById);
    },
    [getVehicleModelById, getManufacturerById]
  );

  /**
   * 取引先名を取得する関数（メモ化済み）
   */
  const getBusinessPartnerName = useCallback(
    (partnerId: string, locationId?: string): string => {
      return getBusinessPartnerNameHelper(
        partnerId,
        locationId,
        getBusinessPartnerById,
        getBusinessPartnerLocationById
      );
    },
    [getBusinessPartnerById, getBusinessPartnerLocationById]
  );

  /**
   * 役割別の取引先名を取得する関数（メモ化済み）
   */
  const getPartnerNameByRole = useCallback(
    (case_: Case, role: 'billing' | 'pickup' | 'delivery'): string => {
      return getPartnerNameByRoleHelper(
        case_,
        role,
        getBusinessPartnerById,
        getBusinessPartnerLocationById,
        clients
      );
    },
    [getBusinessPartnerById, getBusinessPartnerLocationById, clients]
  );

  /**
   * 陸送員名を取得する関数（メモ化済み）
   */
  const getTransporterName = useCallback(
    (transporterId?: string): string => {
      return getTransporterNameHelper(transporterId, landTransporters);
    },
    [landTransporters]
  );

  /**
   * 案件状態のテキスト表現を取得する関数（メモ化済み）
   */
  const getCaseStatusText = useCallback((status: Case['status']): string => {
    return getCaseStatusTextHelper(status);
  }, []);

  /**
   * 案件状態の色クラスを取得する関数（メモ化済み）
   */
  const getCaseStatusColor = useCallback((status: Case['status']): string => {
    return getCaseStatusColorHelper(status);
  }, []);

  /**
   * 各役割の取引先名取得関数（便利関数）
   */
  const getBillingPartnerName = useCallback(
    (case_: Case): string => {
      return getPartnerNameByRole(case_, 'billing');
    },
    [getPartnerNameByRole]
  );

  const getPickupPartnerName = useCallback(
    (case_: Case): string => {
      return getPartnerNameByRole(case_, 'pickup');
    },
    [getPartnerNameByRole]
  );

  const getDeliveryPartnerName = useCallback(
    (case_: Case): string => {
      return getPartnerNameByRole(case_, 'delivery');
    },
    [getPartnerNameByRole]
  );

  /**
   * 引取先の都道府県を取得する（FL-188）
   */
  const getPickupPrefecture = useCallback(
    (case_: Case): string => {
      return getPickupPrefectureHelper(
        case_,
        getBusinessPartnerById,
        getBusinessPartnerLocationById
      );
    },
    [getBusinessPartnerById, getBusinessPartnerLocationById]
  );

  /**
   * データの読み込み状態を考慮した安全な関数群
   * データが読み込まれていない場合は適切なフォールバック値を返します
   */
  const safeHelpers = useMemo(
    () => ({
      /**
       * 安全な車両名取得（データ読み込み中対応）
       */
      getVehicleName: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getVehicleName(case_);
      },

      /**
       * 安全な取引先名取得（データ読み込み中対応）
       */
      getBusinessPartnerName: (partnerId: string, locationId?: string): string => {
        if (loading) return '読み込み中...';
        return getBusinessPartnerName(partnerId, locationId);
      },

      /**
       * 安全な役割別取引先名取得（データ読み込み中対応）
       */
      getPartnerNameByRole: (case_: Case, role: 'billing' | 'pickup' | 'delivery'): string => {
        if (loading) return '読み込み中...';
        return getPartnerNameByRole(case_, role);
      },

      /**
       * 安全な陸送員名取得（データ読み込み中対応）
       */
      getTransporterName: (transporterId?: string): string => {
        if (loading && transporterId) return '読み込み中...';
        return getTransporterName(transporterId);
      },

      /**
       * 安全な請求先名取得
       */
      getBillingPartnerName: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getBillingPartnerName(case_);
      },

      /**
       * 安全な引き取り先名取得
       */
      getPickupPartnerName: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getPickupPartnerName(case_);
      },

      /**
       * 安全な搬入先名取得
       */
      getDeliveryPartnerName: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getDeliveryPartnerName(case_);
      },

      /**
       * 安全な車両名取得（スケジュール画面専用 - メーカー名除外）
       */
      getVehicleNameForSchedule: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getVehicleNameForSchedule(case_);
      },

      /**
       * 安全な引取先都道府県取得（FL-188）
       */
      getPickupPrefecture: (case_: Case): string => {
        if (loading) return '読み込み中...';
        return getPickupPrefecture(case_);
      },
    }),
    [
      loading,
      getVehicleName,
      getVehicleNameForSchedule,
      getBusinessPartnerName,
      getPartnerNameByRole,
      getTransporterName,
      getBillingPartnerName,
      getPickupPartnerName,
      getDeliveryPartnerName,
      getPickupPrefecture,
    ]
  );

  return {
    // 基本的なヘルパー関数
    getVehicleName,
    getVehicleNameForSchedule,
    getBusinessPartnerName,
    getPartnerNameByRole,
    getTransporterName,
    getCaseStatusText,
    getCaseStatusColor,

    // 便利な役割別関数
    getBillingPartnerName,
    getPickupPartnerName,
    getDeliveryPartnerName,
    getPickupPrefecture,

    // 安全なヘルパー関数（読み込み状態対応）
    safe: safeHelpers,

    // その他のユーティリティ
    formatDateForDisplay,

    // データの状態
    loading,
  };
};
